import { useMemo } from "react"

import type { AnimationData, BallState } from "@/types"

export interface PitchPlayer {
  playerId: string
  abbr: string
  teamId: string
  status: "standing" | "prone" | "stunned"
  hasBall: boolean
}

export interface PitchState {
  /** Map from "x,y" → PitchPlayer */
  playerPositions: Map<string, PitchPlayer>
  /** [x, y] or null */
  ballPosition: [number, number] | null
}

/**
 * Computes the initial pitch state from animation data.
 *
 * Scans events for the first KickoffEvent's pitchSnapshot, which contains
 * the starting positions of all players and the ball. Falls back to an
 * empty state if no kickoff snapshot is found.
 */
export function usePitchState(animationData: AnimationData): PitchState {
  return useMemo(() => {
    return computePitchState(animationData)
  }, [animationData])
}

export function computePitchState(animationData: AnimationData): PitchState {
  // Find first event with a non-null pitchSnapshot (KickoffEvent)
  const kickoffEvent = animationData.events.find(
    (e) => e.pitchSnapshot !== null && e.pitchSnapshot !== undefined,
  )

  if (!kickoffEvent?.pitchSnapshot) {
    return { playerPositions: new Map(), ballPosition: null }
  }

  const { players, ball } = kickoffEvent.pitchSnapshot

  const playerPositions = new Map<string, PitchPlayer>()
  const ballPosition = ballCoords(ball)

  for (const snapshot of players) {
    const key = `${snapshot.x},${snapshot.y}`
    const playerData = animationData.players[snapshot.id]

    playerPositions.set(key, {
      playerId: snapshot.id,
      abbr: playerData?.abbr ?? "?",
      teamId: snapshot.teamId,
      status: normalizeStatus(snapshot.status),
      hasBall: ball?.carrierId === snapshot.id,
    })
  }

  return { playerPositions, ballPosition }
}

function ballCoords(ball: BallState | null): [number, number] | null {
  if (!ball) return null
  return [ball.x, ball.y]
}

function normalizeStatus(
  status: string | undefined,
): "standing" | "prone" | "stunned" {
  if (status === "prone") return "prone"
  if (status === "stunned") return "stunned"
  return "standing"
}
